import { ChangeAttrName } from '@common/flare/change-attr-name.enum';
import { ToFlareXMLOptions } from '@common/flare/types/to-flare-xml-options.type';
import { defaultToFlareXML, fragmentToArray } from '@common/flare/util/prosemirror-flare-xml';
import { applyChange } from '@common/prosemirror/changeset/tracked-changes';
import { getMark } from '@common/prosemirror/model/mark';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { TrackedChange } from '@common/prosemirror/tracked-changes/tracked-change.type';
import { getTrackedChangeIdsNodeAttr, toTrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-changes';
import { nodeToDOM } from '@common/util/schema';
import { NodeSpec, ProseMirrorNode } from 'prosemirror-model';

function transferChangesToChildNode(node: ProseMirrorNode, childNode: ProseMirrorNode, changeAttrs: TrackedChangeNodeAttrs): ProseMirrorNode {
  let existingAttrs: TrackedChangeNodeAttrs;
  const changeMarkType = node.type.schema.marks['madcapchange'];

  // If the child is a text node then wrap it in a MadCap:change node
  if (childNode.type.name === 'text') {
    const changeMark = getMark(childNode, changeMarkType);
    if (changeMark) {
      existingAttrs = toTrackedChangeNodeAttrs(changeMark.attrs);
    }
  } else {
    existingAttrs = toTrackedChangeNodeAttrs(childNode.attrs);
  }

  if (Array.isArray(changeAttrs.changeList)) {
    changeAttrs.changeList.forEach(trackedChange => {
      existingAttrs = applyChange(existingAttrs, trackedChange);
    });
  }

  if (existingAttrs) {
    if (childNode.type.name === 'text') {
      childNode = childNode.mark([changeMarkType.create(existingAttrs)]);
    } else {
      childNode = childNode.type.create({ ...node.attrs, ...existingAttrs }, childNode.content, childNode.marks);
    }
  } else {
    childNode = null;
  }

  return childNode;
}

/**
 * McCentralContainerSchemaPlugin
 * The mc-central-container is used to wrap inline content in a block node when that inline content is inside a node that only allows block content.
 * The mc-central-container schema also defines a node that is used as the default auto-inserted block node when running in the server environment.
 */
export class McCentralContainerSchemaPlugin extends SchemaPlugin {
  nodes = {
    mcCentralContainer: {
      group: 'block',
      content: 'inline*',
      passthroughBlock: true,
      parseDOM: [
        // Parse <mc-central-container> because it is used when serializing content to the clipboard.
        // This makes it possible to copy and paste plain text within the editor. Otherwise the plain text would get wrapped in a <p> tag on paste.
        {
          tag: 'mc-central-container',
          preserveWhitespace: true
        }
      ],
      toDOM(node) { return nodeToDOM(node); },
      toFlareXML(node: ProseMirrorNode, options: ToFlareXMLOptions): string {
        let xml = '';

        let children: ProseMirrorNode[] = fragmentToArray(node.content);
        // Use getTrackedChangeIdsNodeAttr so legacy nodes that have changeList but no MadCap:changes still produce a usable id
        const changeIds: string = getTrackedChangeIdsNodeAttr(node.attrs);
        const changeList: TrackedChange[] = node.attrs.changeList;

        // If there are changes then move them to the child nodes
        if (changeList?.length > 0) {
          children = children
            // Transfer the changes to each child node
            .map(childNode => transferChangesToChildNode(node, childNode, { [ChangeAttrName.ChangeIds]: changeIds, changeList }))
            // Filter out any children that were removed from the transfer
            .filter(childNode => !!childNode);
        }

        // Render the children
        for (let i = 0; i < children.length; i += 1) {
          const child = children[i];
          xml += (child.type.spec.toFlareXML ?? defaultToFlareXML)(child, options)
        }

        return xml;
      },
      tagName: 'mc-central-container',
      specSkip: true
    }
  } satisfies Record<string, NodeSpec>;
}
